import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, PackageCheck, PackageX, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import appApi from "./appApi";
import { Skeleton, stagger, rise, spring } from "./bits";

const STATUS_STYLE = {
  new: "bg-amber-100 text-amber-700",
  preparing: "bg-sky-100 text-sky-700",
  ready: "bg-emerald-100 text-emerald-700",
  completed: "bg-stone-200 text-stone-600",
  cancelled: "bg-rose-100 text-rose-600",
  extended: "bg-purple-100 text-purple-700",
  awaiting_supplier: "bg-orange-100 text-orange-700",
  supply_to_order: "bg-orange-100 text-orange-700",
  supply_ordered: "bg-indigo-100 text-indigo-700",
  supply_received: "bg-teal-100 text-teal-700",
};

const QUICK_NEXT = { new: ["preparing", "ready", "cancelled"], preparing: ["ready", "cancelled"], ready: ["completed", "cancelled"] };

const OrdersScreen = () => {
  const { t, lang } = useLang();
  const m = t.mapp;
  const orderStatuses = t.account.order_status;
  const [orders, setOrders] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    appApi.get("/app/orders").then((r) => setOrders(r.data)).catch(() => toast.error(m.error_generic));
  }, [m.error_generic]);
  useEffect(load, [load]);

  const setStatus = async (o, status) => {
    try {
      await appApi.patch(`/app/orders/${o.id}/status`, { status });
      toast.success(`${o.order_number} → ${orderStatuses[status] || status}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || m.error_generic);
    }
  };

  return (
    <div data-testid="app-orders-screen">
      <h1 className="font-heading text-3xl font-medium text-brand-ink">{m.tab_orders}</h1>
      {orders === null ? (
        <div className="mt-5 space-y-3"><Skeleton className="h-24" /><Skeleton className="h-24" /></div>
      ) : orders.length === 0 ? (
        <p className="text-sm text-brand-ink/40 italic py-10 text-center">{m.orders_empty}</p>
      ) : (
        <motion.div variants={stagger} initial="hidden" animate="show" className="mt-5 space-y-3">
          {orders.map((o) => (
            <motion.div key={o.id} variants={rise} className="rounded-2xl bg-white border border-brand/10 p-4 shadow-sm" data-testid={`app-order-${o.id}`}>
              <button type="button" className="w-full text-left" onClick={() => setOpenId(openId === o.id ? null : o.id)}>
                <div className="flex items-center gap-3">
                  <div className="grid place-items-center h-11 w-11 rounded-xl bg-amber-50 text-amber-600 shrink-0">
                    <ShoppingBag className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-brand-ink">{o.order_number} <span className="font-normal text-brand-ink/55">· {o.first_name} {o.last_name}</span></p>
                    <p className="text-xs text-brand-ink/55">{(o.created_at || "").slice(0, 10)} · {o.items?.length || 0} τεμ. · €{(o.total ?? 0).toFixed(2)}</p>
                  </div>
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full shrink-0 ${STATUS_STYLE[o.status] || STATUS_STYLE.new}`}>
                    {orderStatuses[o.status] || o.status}
                  </span>
                </div>
              </button>
              {openId === o.id && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="overflow-hidden">
                  <div className="pt-3 mt-3 border-t border-brand/10 space-y-1.5">
                    {(o.items || []).map((it, i) => (
                      <p key={i} className="text-xs text-brand-ink/70">· {lang === "el" ? it.name_gr : (it.name_en || it.name_gr)}{it.variant_label ? ` (${it.variant_label})` : ""} × {it.qty} — €{(it.price * it.qty).toFixed(2)}</p>
                    ))}
                    <a href={`tel:${o.phone}`} className="inline-block text-xs font-bold text-brand mt-1">{o.phone}</a>
                    {(QUICK_NEXT[o.status] || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-2">
                        {QUICK_NEXT[o.status].map((st) => (
                          <motion.button
                            key={st}
                            whileTap={{ scale: 0.94 }}
                            onClick={() => setStatus(o, st)}
                            className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft/70 text-brand text-xs font-bold px-3.5 py-2"
                            data-testid={`app-order-${o.id}-${st}`}
                          >
                            {st === "cancelled" ? <PackageX className="h-3.5 w-3.5" /> : <PackageCheck className="h-3.5 w-3.5" />}
                            {orderStatuses[st] || st}
                          </motion.button>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default OrdersScreen;
